import { useState } from "react";
import {
  LayoutDashboard,
  Star,
  MessageSquare,
  Bell,
  Settings,
  CreditCard,
  ChevronDown,
  ShieldCheck,
  Plus,
} from "lucide-react";
import { NavLink, useNavigate } from "react-router-dom";

import { useAuth } from "../../../context/AuthContext";
import { useBusiness } from "../../../context/BusinessContext";

const navItems = [
  { name: "Overview", icon: LayoutDashboard, path: "/overview" },
  { name: "Reviews", icon: Star, path: "/reviews" },
  { name: "Responses", icon: MessageSquare, path: "/responses" },
  { name: "Notifications", icon: Bell, path: "/notifications" },
  { name: "Settings", icon: Settings, path: "/settings" },
  { name: "Billing", icon: CreditCard, path: "/billing" },
];

export default function Sidebar({ onNavigate }) {
  const navigate = useNavigate();

  const { user, initials } = useAuth();

  const {
    businesses,
    selectedBusiness,
    setSelectedBusiness,
    loading: businessesLoading,
  } = useBusiness();

  const [businessOpen, setBusinessOpen] = useState(false);

  function getBusinessInitials(name) {
    if (!name) return "+";

    return name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0])
      .join("")
      .toUpperCase();
  }

  function handleAddBusiness() {
    setBusinessOpen(false);

    navigate("/onboarding/business");

    if (onNavigate) {
      onNavigate();
    }
  }

  return (
    <aside className="sticky top-0 h-screen w-[280px] shrink-0 bg-gradient-to-b from-slate-950 to-blue-950 text-white flex flex-col border-r border-white/10">
      {/* Logo */}
      <div className="px-6 py-6">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl flex items-center justify-center bg-white/10">
            <ShieldCheck size={24} />
          </div>

          <h1 className="font-heading text-2xl font-bold tracking-tight">
            RevUShield
          </h1>
        </div>
      </div>

      {/* Business Selector */}
      <div className="px-4 py-5 relative">
        <button
          type="button"
          onClick={() => setBusinessOpen((prev) => !prev)}
          className="w-full bg-white/10 hover:bg-white/15 transition rounded-2xl p-3 flex items-center justify-between"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-12 w-12 rounded-xl bg-slate-700 flex items-center justify-center text-sm font-semibold shrink-0">
              {selectedBusiness ? (
                getBusinessInitials(selectedBusiness.name)
              ) : (
                <Plus size={20} />
              )}
            </div>

            <div className="text-left min-w-0">
              <p className="font-semibold text-sm truncate">
                {businessesLoading
                  ? "Loading..."
                  : selectedBusiness
                    ? selectedBusiness.name
                    : "Add Business"}
              </p>

              <p className="text-xs text-slate-300 truncate">
                {selectedBusiness
                  ? selectedBusiness.industry || "Business profile"
                  : "Set up your first business"}
              </p>
            </div>
          </div>

          <ChevronDown
            size={18}
            className={`shrink-0 transition ${
              businessOpen ? "rotate-180" : ""
            }`}
          />
        </button>

        {businessOpen && (
          <div className="absolute left-4 right-4 top-[92px] z-20 rounded-2xl border border-white/10 bg-slate-900 shadow-2xl overflow-hidden">
            {businesses.length > 0 &&
              businesses.map((business) => (
                <button
                  key={business.id}
                  type="button"
                  onClick={() => {
                    setSelectedBusiness(business);
                    setBusinessOpen(false);

                    if (onNavigate) {
                      onNavigate();
                    }
                  }}
                  className={`
                    w-full flex items-center gap-3 px-3 py-3 text-left transition
                    ${
                      selectedBusiness?.id === business.id
                        ? "bg-white/10"
                        : "hover:bg-white/10"
                    }
                  `}
                >
                  <div className="h-10 w-10 rounded-xl bg-slate-700 flex items-center justify-center text-xs font-semibold">
                    {getBusinessInitials(business.name)}
                  </div>

                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">
                      {business.name}
                    </p>

                    <p className="text-xs text-slate-400 truncate">
                      {business.industry || "Business profile"}
                    </p>
                  </div>
                </button>
              ))}

            {businesses.length > 0 && <div className="h-px bg-white/10" />}

            <button
              type="button"
              onClick={handleAddBusiness}
              className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-white/10 transition"
            >
              <div className="h-10 w-10 rounded-xl bg-indigo-600 flex items-center justify-center">
                <Plus size={18} />
              </div>

              <div>
                <p className="text-sm font-medium">Add Business</p>

                <p className="text-xs text-slate-400">
                  Start onboarding another business
                </p>
              </div>
            </button>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 space-y-2 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;

          return (
            <NavLink
              key={item.name}
              to={item.path}
              onClick={onNavigate}
              className={({ isActive }) =>
                `
                  w-full flex items-center gap-3 px-4 py-3 rounded-xl transition
                  ${
                    isActive
                      ? "bg-indigo-600 text-white shadow-lg shadow-indigo-600/20"
                      : "text-slate-300 hover:bg-white/10 hover:text-white"
                  }
                `
              }
            >
              <Icon size={20} />

              <span className="font-medium text-sm">{item.name}</span>
            </NavLink>
          );
        })}
      </nav>

      {/* User Profile */}
      <div className="p-4 border-t border-white/10">
        <div className="bg-white/5 rounded-2xl p-3 flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-slate-500 flex items-center justify-center font-bold">
            {initials || "?"}
          </div>

          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">
              {user?.fullName || "Loading..."}
            </p>

            <p className="text-xs text-slate-400 truncate">
              {user?.email || ""}
            </p>
          </div>
        </div>
      </div>
    </aside>
  );
}
